import { useState } from "react";
import { toast } from "sonner";
import { Plus, Wallet, Download, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState } from "@/components/admin/AdminUI";
import { fmt, useList, Field, inputCls, COUNTRY } from "@/components/admin/finance/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PeriodFilter, periodQuery, downloadCsv } from "@/components/admin/finance/Period";

export default function Treasury() {
  const { data: accounts, reload } = useList(`/finance/treasury/accounts?country=${COUNTRY}`);
  const [period, setPeriod] = useState({ from: "", to: "" });
  const pq = periodQuery(period);
  const suffix = period.from || period.to ? `_${period.from || "debut"}_${period.to || "fin"}` : "";
  const { data: movements } = useList(`/finance/treasury/movements?country=${COUNTRY}${pq}`, [pq]);
  const [form, setForm] = useState(null);
  const openNew = () => setForm({ name: "", currency: "FCFA", balance: "" });
  const save = async () => {
    if (!form.name) return toast.error("Nom requis");
    try {
      if (form.id) await api.put(`/finance/treasury/accounts/${form.id}`, { name: form.name, currency: form.currency });
      else await api.post("/finance/treasury/accounts", { ...form, balance: Number(form.balance || 0), country: COUNTRY });
      toast.success(form.id ? "Compte modifié" : "Compte créé"); setForm(null); reload();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
        <PeriodFilter value={period} onChange={setPeriod} testid="treasury-period" />
        <div className="flex gap-2">
        <button onClick={() => downloadCsv(`/finance/export/treasury.csv?country=${COUNTRY}${pq}`, `tresorerie_${COUNTRY}${suffix}.csv`)} data-testid="export-treasury-csv" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4CB944] text-white text-sm font-semibold hover:bg-[#3AA033]"><Download className="w-4 h-4" /> Export (CSV)</button>
        <button onClick={openNew} data-testid="account-add-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold"><Plus className="w-4 h-4" /> Nouveau compte</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-6" data-testid="treasury-accounts">
        {accounts.map((a) => (
          <Card key={a.id} className="p-5"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-[#4CB944]/15 grid place-items-center"><Wallet className="w-5 h-5 text-[#4CB944]" /></div><div className="flex-1 min-w-0"><div className="text-sm text-slate-400 truncate">{a.name}</div><div className="text-xl font-bold text-white">{fmt(a.balance, a.currency)}</div></div><button onClick={() => setForm({ id: a.id, name: a.name, currency: a.currency })} data-testid={`account-edit-${a.id}`} className="p-1.5 rounded-lg text-slate-500 hover:text-white"><Pencil className="w-4 h-4" /></button></div></Card>
        ))}
        {accounts.length === 0 && <Card className="sm:col-span-3"><EmptyState text="Aucun compte." /></Card>}
      </div>
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-[#1E293B] text-sm font-semibold text-white">Mouvements (décaissements)</div>
        {movements.length === 0 ? <EmptyState text="Aucun mouvement enregistré." /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="movements-table">
            <thead><tr className="border-b border-[#1E293B] text-left text-slate-400"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Compte</th><th className="px-4 py-3">Réf. dépense</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3 text-right">Montant</th></tr></thead>
            <tbody>{movements.map((m) => (
              <tr key={m.id} className="border-b border-[#1E293B]/60">
                <td className="px-4 py-3 text-slate-400">{m.date}</td>
                <td className="px-4 py-3 text-slate-300">{m.account_name}</td>
                <td className="px-4 py-3 text-white font-mono text-xs">{m.expense_ref}</td>
                <td className="px-4 py-3 text-slate-400">{m.mode}</td>
                <td className="px-4 py-3 text-right text-rose-400">- {fmt(m.amount, m.currency)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="dark bg-[#111827] border-[#1E293B]">
          <DialogHeader><DialogTitle className="text-white">{form?.id ? "Modifier le compte" : "Nouveau compte de trésorerie"}</DialogTitle></DialogHeader>
          {form && <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom" full><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
            <Field label="Devise"><select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputCls}><option>FCFA</option><option>EUR</option><option>USD</option></select></Field>
            {!form.id && <Field label="Solde initial"><input type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className={inputCls} /></Field>}
          </div>}
          <DialogFooter><button onClick={() => setForm(null)} className="px-4 py-2.5 rounded-xl border border-[#1E293B] text-slate-300 text-sm">Annuler</button><button onClick={save} data-testid="account-save-button" className="px-5 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold">{form?.id ? "Enregistrer" : "Créer"}</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
